import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Mail, Plus, Settings, FileText, History, Send, CheckCircle, XCircle, 
  AlertCircle, Trash2, Edit, TestTube, RefreshCw, Server, Lock, Eye, EyeOff
} from 'lucide-react';
import api from '@/lib/axios';
import { formatDate, formatDateTime } from '@/lib/utils';

export default function EmailPage() {
  const [activeTab, setActiveTab] = useState('configs');
  const [stats, setStats] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configsForbidden, setConfigsForbidden] = useState(false);
  
  // Dialogs
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [testConfig, setTestConfig] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    config_name: '',
    smtp_server: 'smtp.strato.de',
    smtp_port: 465,
    smtp_security: 'SSL',
    smtp_user: '',
    smtp_password: '',
    sender_email: '',
    sender_name: '',
    reply_to: '',
    is_default: false,
    use_for_invoices: false,
    use_for_portal: false,
    use_for_reminders: false,
    role_names: [],
    is_active: true,
    signature_html: ''
  });
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    template_key: '',
    name: '',
    description: '',
    subject: '',
    html_body: '',
    plain_body: '',
    available_variables: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, configsRes, templatesRes, logsRes] = await Promise.allSettled([
        api.get('/email/stats'),
        api.get('/email/configs'),
        api.get('/email/templates'),
        api.get('/email/logs?limit=50')
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (configsRes.status === 'fulfilled') {
        setConfigs(configsRes.value.data);
        setConfigsForbidden(false);
      } else {
        setConfigs([]);
        setConfigsForbidden(true);
      }
      if (templatesRes.status === 'fulfilled') setTemplates(templatesRes.value.data);
      if (logsRes.status === 'fulfilled') setLogs(logsRes.value.data);
      if ([statsRes, configsRes, templatesRes, logsRes].every((result) => result.status === 'rejected')) {
        toast.error('Fehler beim Laden der E-Mail-Daten');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==================== Config Functions ====================
  
  const openConfigDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setConfigForm({
        config_name: config.config_name,
        smtp_server: config.smtp_server,
        smtp_port: config.smtp_port,
        smtp_security: config.smtp_security,
        smtp_user: config.smtp_user,
        smtp_password: '', // Don't show existing password
        sender_email: config.sender_email,
        sender_name: config.sender_name,
        reply_to: config.reply_to || '',
        is_default: config.is_default,
        use_for_invoices: config.use_for_invoices,
        use_for_portal: config.use_for_portal,
        use_for_reminders: config.use_for_reminders,
        role_names: config.role_names || [],
        is_active: config.is_active,
        signature_html: config.signature_html || ''
      });
    } else {
      setEditingConfig(null);
      setConfigForm({
        config_name: '',
        smtp_server: 'smtp.strato.de',
        smtp_port: 465,
        smtp_security: 'SSL',
        smtp_user: '',
        smtp_password: '',
        sender_email: '',
        sender_name: '',
        reply_to: '',
        is_default: configs.length === 0,
        use_for_invoices: false,
        use_for_portal: false,
        use_for_reminders: false,
        role_names: [],
        is_active: true,
        signature_html: ''
      });
    }
    setShowPassword(false);
    setShowConfigDialog(true);
  };

  const saveConfig = async () => {
    try {
      const body = { ...configForm };
      if (editingConfig && !body.smtp_password) {
        delete body.smtp_password; // Don't update password if empty
      }
      
      if (editingConfig) {
        await api.put(`/email/configs/${editingConfig.id}`, body);
      } else {
        await api.post('/email/configs', body);
      }
      
      toast.success(editingConfig ? 'Konfiguration aktualisiert' : 'Konfiguration erstellt');
      setShowConfigDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const deleteConfig = async (configId) => {
    if (!window.confirm('Konfiguration wirklich löschen?')) return;
    
    try {
      await api.delete(`/email/configs/${configId}`);
      toast.success('Konfiguration gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const testConfiguration = async () => {
    if (!testEmail) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    
    setTestLoading(true);
    try {
      const res = await api.post(`/email/configs/${testConfig.id}/test?test_recipient=${encodeURIComponent(testEmail)}`);
      
      if (res.data.success) {
        toast.success('Test-E-Mail erfolgreich gesendet!');
        setShowTestDialog(false);
        fetchData();
      } else {
        toast.error(res.data.error_detail || 'Test fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler beim Testen');
    } finally {
      setTestLoading(false);
    }
  };

  // ==================== Template Functions ====================

  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        template_key: template.template_key,
        name: template.name,
        description: template.description || '',
        subject: template.subject,
        html_body: template.html_body,
        plain_body: template.plain_body || '',
        available_variables: template.available_variables || ''
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        template_key: '',
        name: '',
        description: '',
        subject: '',
        html_body: '',
        plain_body: '',
        available_variables: ''
      });
    }
    setShowTemplateDialog(true);
  };

  const saveTemplate = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/email/templates/${editingTemplate.id}`, templateForm);
      } else {
        await api.post('/email/templates', templateForm);
      }
      
      toast.success(editingTemplate ? 'Vorlage aktualisiert' : 'Vorlage erstellt');
      setShowTemplateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    
    try {
      await api.delete(`/email/templates/${templateId}`);
      toast.success('Vorlage gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const res = await api.post('/email/templates/seed-defaults');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Standard-Vorlagen');
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="email-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="h-6 w-6" />
              E-Mail-System
            </h1>
            <p className="text-muted-foreground">
              SMTP-Konfigurationen und E-Mail-Vorlagen verwalten
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="restore-sender-banner-btn"
              onClick={() => {
                localStorage.removeItem('email_sender_banner_hidden');
                window.dispatchEvent(new Event('show-sender-banner'));
                toast.success('Absender-Banner wieder aktiviert');
              }}
              variant="outline"
              size="sm"
            >
              Sender-Banner einblenden
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{stats.total_configs}</div>
                <div className="text-sm text-muted-foreground">Konfigurationen</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600">{stats.active_configs}</div>
                <div className="text-sm text-muted-foreground">Aktiv</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{stats.total_templates}</div>
                <div className="text-sm text-muted-foreground">Vorlagen</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{stats.total_sent}</div>
                <div className="text-sm text-muted-foreground">Gesendet (gesamt)</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600">{stats.sent_today}</div>
                <div className="text-sm text-muted-foreground">Heute gesendet</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-red-600">{stats.failed_today}</div>
                <div className="text-sm text-muted-foreground">Heute fehlgeschlagen</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="configs" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              SMTP-Konfigurationen
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Vorlagen
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Verlauf
            </TabsTrigger>
          </TabsList>

          {/* Configs Tab */}
          <TabsContent value="configs" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Verwalten Sie hier Ihre SMTP-Server-Konfigurationen für verschiedene E-Mail-Konten.
              </p>
              <Button onClick={() => openConfigDialog()} data-testid="add-config-btn">
                <Plus className="h-4 w-4 mr-2" />
                Neue Konfiguration
              </Button>
            </div>

            {configsForbidden ? (
              <Card>
                <CardContent className="p-8 text-center" data-testid="email-configs-admin-only">
                  <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Nur für Administratoren</h3>
                </CardContent>
              </Card>
            ) : configs.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Keine Konfigurationen</h3>
                  <p className="text-muted-foreground mb-4">
                    Erstellen Sie Ihre erste SMTP-Konfiguration, um E-Mails versenden zu können.
                  </p>
                  <Button onClick={() => openConfigDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Konfiguration erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {configs.map(config => (
                  <Card key={config.id} data-testid={`config-card-${config.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{config.config_name}</CardTitle>
                          {config.is_default && <Badge variant="secondary">Standard</Badge>}
                          {!config.is_active && <Badge variant="destructive">Inaktiv</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setTestConfig(config);
                              setTestEmail('');
                              setShowTestDialog(true);
                            }}
                            data-testid={`test-config-${config.id}`}
                          >
                            <TestTube className="h-4 w-4 mr-1" />
                            Testen
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`edit-config-${config.id}`}
                            onClick={() => openConfigDialog(config)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`delete-config-${config.id}`}
                            onClick={() => deleteConfig(config.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <CardDescription>
                        {config.smtp_server}:{config.smtp_port} ({config.smtp_security})
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Absender:</span>
                          <div>{config.sender_name} &lt;{config.sender_email}&gt;</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Benutzer:</span>
                          <div>{config.smtp_user}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Verwendung:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {config.use_for_invoices && <Badge variant="outline">Rechnungen</Badge>}
                            {config.use_for_portal && <Badge variant="outline">Portal</Badge>}
                            {config.use_for_reminders && <Badge variant="outline">Erinnerungen</Badge>}
                            {(config.role_names || []).map(r => (
                              <Badge key={r} variant="secondary" data-testid={`config-role-${r}`}>{r}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Letzter Test:</span>
                          <div className="flex items-center gap-1 mt-1">
                            {config.last_test_at ? (
                              <>
                                {config.last_test_success ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-500" />
                                )}
                                {formatDate(config.last_test_at)}
                              </>
                            ) : (
                              <span className="text-muted-foreground">Noch nicht getestet</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                E-Mail-Vorlagen für Rechnungen, Portal-Aktivierung und Erinnerungen.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={seedDefaultTemplates}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Standard-Vorlagen
                </Button>
                <Button onClick={() => openTemplateDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neue Vorlage
                </Button>
              </div>
            </div>

            {templates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Keine Vorlagen</h3>
                  <p className="text-muted-foreground mb-4">
                    Erstellen Sie Standard-Vorlagen für häufig verwendete E-Mails.
                  </p>
                  <Button onClick={seedDefaultTemplates}>
                    Standard-Vorlagen erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {templates.map(template => (
                  <Card key={template.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription>
                            Schlüssel: {template.template_key}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTemplateDialog(template)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteTemplate(template.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm">
                        <div className="mb-2">
                          <span className="text-muted-foreground">Betreff:</span> {template.subject}
                        </div>
                        {template.description && (
                          <div className="text-muted-foreground">{template.description}</div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Verlauf der gesendeten E-Mails (letzte 50 Einträge)
            </p>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Empfänger</TableHead>
                    <TableHead>Betreff</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Zeitpunkt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Noch keine E-Mails gesendet
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {log.status === 'sent' ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Gesendet
                            </Badge>
                          ) : log.status === 'queued' ? (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-brand-red/10" data-testid={`log-queued-${log.id}`}>
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Wartet auf Freigabe
                            </Badge>
                          ) : log.status === 'failed' ? (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Fehlgeschlagen
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Ausstehend
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{log.recipient}</TableCell>
                        <TableCell className="max-w-xs truncate">{log.subject}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.context_type || 'Allgemein'}</Badge>
                        </TableCell>
                        <TableCell>
                          {formatDateTime(log.queued_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Config Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingConfig ? 'Konfiguration bearbeiten' : 'Neue SMTP-Konfiguration'}
              </DialogTitle>
              <DialogDescription>
                Konfigurieren Sie die SMTP-Einstellungen für den E-Mail-Versand.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* Name */}
              <div className="grid gap-2">
                <Label>Bezeichnung *</Label>
                <Input
                  value={configForm.config_name}
                  onChange={e => setConfigForm({...configForm, config_name: e.target.value})}
                  placeholder="z.B. Rechnungen, Benachrichtigungen"
                  data-testid="config-name-input"
                />
              </div>

              {/* SMTP Server Settings */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 grid gap-2">
                  <Label>SMTP-Server *</Label>
                  <Input
                    value={configForm.smtp_server}
                    onChange={e => setConfigForm({...configForm, smtp_server: e.target.value})}
                    placeholder="smtp.strato.de"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Port *</Label>
                  <Input
                    type="number"
                    value={configForm.smtp_port}
                    onChange={e => setConfigForm({...configForm, smtp_port: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Sicherheit</Label>
                  <Select
                    value={configForm.smtp_security}
                    onValueChange={v => setConfigForm({...configForm, smtp_security: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SSL">SSL (Port 465)</SelectItem>
                      <SelectItem value="STARTTLS">STARTTLS (Port 587)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Credentials */}
              <div className="grid gap-2">
                <Label>SMTP-Benutzer (E-Mail) *</Label>
                <Input
                  value={configForm.smtp_user}
                  onChange={e => setConfigForm({...configForm, smtp_user: e.target.value})}
                  placeholder="info@musikschule.de"
                />
              </div>

              <div className="grid gap-2">
                <Label>
                  SMTP-Passwort {editingConfig && '(leer lassen, um beizubehalten)'}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={configForm.smtp_password}
                    onChange={e => setConfigForm({...configForm, smtp_password: e.target.value})}
                    placeholder={editingConfig ? "••••••••" : "Passwort eingeben"}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Sender Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Absender-Name *</Label>
                  <Input
                    value={configForm.sender_name}
                    onChange={e => setConfigForm({...configForm, sender_name: e.target.value})}
                    placeholder="Musikschule Musterstadt"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Absender-E-Mail *</Label>
                  <Input
                    value={configForm.sender_email}
                    onChange={e => setConfigForm({...configForm, sender_email: e.target.value})}
                    placeholder="info@musikschule.de"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Antwort-Adresse (optional)</Label>
                <Input
                  value={configForm.reply_to}
                  onChange={e => setConfigForm({...configForm, reply_to: e.target.value})}
                  placeholder="antwort@musikschule.de"
                />
              </div>

              {/* Signatur */}
              <div className="border rounded-lg p-4 space-y-3">
                <div>
                  <Label className="text-base">E-Mail-Signatur (HTML)</Label>
                  <p className="text-sm text-muted-foreground">
                    Wird automatisch an alle ausgehenden E-Mails dieser Konfiguration angehängt.
                    HTML erlaubt (z.&nbsp;B. &lt;b&gt;, &lt;br&gt;, &lt;a href&gt;, &lt;img src&gt;).
                  </p>
                </div>
                <Textarea
                  rows={10}
                  value={configForm.signature_html}
                  onChange={e => setConfigForm({...configForm, signature_html: e.target.value})}
                  placeholder="<p><b>Musik Akademie Meyersick gGmbH</b><br>…</p>"
                  className="font-mono text-xs"
                  data-testid="config-signature-input"
                />
                {configForm.signature_html?.trim() ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">Vorschau</Label>
                    <div
                      className="mt-1 border border-dashed rounded-lg p-4 bg-white overflow-auto max-h-[300px]"
                      data-testid="config-signature-preview"
                      dangerouslySetInnerHTML={{ __html: configForm.signature_html }}
                    />
                  </div>
                ) : (
                  <div
                    className="border border-dashed rounded-lg p-4 text-sm text-muted-foreground italic"
                    data-testid="config-signature-preview"
                  >
                    Keine Signatur hinterlegt – Vorschau erscheint hier.
                  </div>
                )}
              </div>

              {/* Usage Flags */}
              <div className="border rounded-lg p-4 space-y-4">
                <Label className="text-base">Verwendung</Label>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Standard-Konfiguration</div>
                    <div className="text-sm text-muted-foreground">
                      Wird verwendet, wenn keine spezifische Konfiguration zugewiesen ist
                    </div>
                  </div>
                  <Switch
                    checked={configForm.is_default}
                    onCheckedChange={v => setConfigForm({...configForm, is_default: v})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Für Rechnungen</div>
                    <div className="text-sm text-muted-foreground">
                      Diese Konfiguration für Rechnungsversand verwenden
                    </div>
                  </div>
                  <Switch
                    checked={configForm.use_for_invoices}
                    onCheckedChange={v => setConfigForm({...configForm, use_for_invoices: v})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Für Portal-Aktivierung</div>
                    <div className="text-sm text-muted-foreground">
                      Diese Konfiguration für Portal-Einladungen verwenden
                    </div>
                  </div>
                  <Switch
                    checked={configForm.use_for_portal}
                    onCheckedChange={v => setConfigForm({...configForm, use_for_portal: v})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Für Terminerinnerungen</div>
                    <div className="text-sm text-muted-foreground">
                      Diese Konfiguration für Erinnerungen verwenden
                    </div>
                  </div>
                  <Switch
                    checked={configForm.use_for_reminders}
                    onCheckedChange={v => setConfigForm({...configForm, use_for_reminders: v})}
                  />
                </div>

                {/* Role-based selection */}
                <div className="border-t pt-3 mt-2">
                  <div className="font-medium mb-1">Rollen, die diesen Absender nutzen</div>
                  <div className="text-sm text-muted-foreground mb-2">
                    Wenn ein Nutzer mit einer der angekreuzten Rollen eingeloggt ist und
                    aus dem System eine E-Mail verschickt, wird automatisch dieser Absender genutzt.
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-testid="role-checkbox-group">
                    {[
                      { key: 'admin', label: 'Admin' },
                      { key: 'secretary', label: 'Sekretariat' },
                      { key: 'accounting', label: 'Buchhaltung' },
                      { key: 'management', label: 'Verwaltung / Pädagogische Leitung' },
                      { key: 'teacher', label: 'Lehrer' },
                    ].map(r => {
                      const checked = (configForm.role_names || []).includes(r.key);
                      return (
                        <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`role-${r.key}`}>
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              const set = new Set(configForm.role_names || []);
                              if (e.target.checked) set.add(r.key); else set.delete(r.key);
                              setConfigForm({ ...configForm, role_names: Array.from(set) });
                            }} />
                          {r.label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Aktiv</div>
                    <div className="text-sm text-muted-foreground">
                      Konfiguration ist für den Versand verfügbar
                    </div>
                  </div>
                  <Switch
                    checked={configForm.is_active}
                    onCheckedChange={v => setConfigForm({...configForm, is_active: v})}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveConfig} data-testid="save-config-btn">
                {editingConfig ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>SMTP-Konfiguration testen</DialogTitle>
              <DialogDescription>
                Senden Sie eine Test-E-Mail, um die Konfiguration zu überprüfen.
              </DialogDescription>
            </DialogHeader>

            {testConfig && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-muted rounded-lg text-sm">
                  <div><strong>Konfiguration:</strong> {testConfig.config_name}</div>
                  <div><strong>Server:</strong> {testConfig.smtp_server}:{testConfig.smtp_port}</div>
                  <div><strong>Absender:</strong> {testConfig.sender_email}</div>
                </div>

                <div className="grid gap-2">
                  <Label>Test-Empfänger *</Label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="ihre-email@example.com"
                    data-testid="test-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    An diese Adresse wird eine Test-E-Mail gesendet.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={testConfiguration} disabled={testLoading} data-testid="send-test-btn">
                {testLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sende...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Test senden
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Vorlage bearbeiten' : 'Neue E-Mail-Vorlage'}
              </DialogTitle>
              <DialogDescription>
                Erstellen Sie eine Vorlage mit Platzhaltern für dynamische Inhalte.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Schlüssel *</Label>
                  <Input
                    value={templateForm.template_key}
                    onChange={e => setTemplateForm({...templateForm, template_key: e.target.value})}
                    placeholder="z.B. invoice, reminder"
                    disabled={!!editingTemplate}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Name *</Label>
                  <Input
                    value={templateForm.name}
                    onChange={e => setTemplateForm({...templateForm, name: e.target.value})}
                    placeholder="z.B. Rechnung, Terminerinnerung"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Beschreibung</Label>
                <Input
                  value={templateForm.description}
                  onChange={e => setTemplateForm({...templateForm, description: e.target.value})}
                  placeholder="Wann wird diese Vorlage verwendet?"
                />
              </div>

              <div className="grid gap-2">
                <Label>Betreff *</Label>
                <Input
                  value={templateForm.subject}
                  onChange={e => setTemplateForm({...templateForm, subject: e.target.value})}
                  placeholder="Verwenden Sie {variable} für Platzhalter"
                />
              </div>

              <div className="grid gap-2">
                <Label>HTML-Inhalt *</Label>
                <Textarea
                  value={templateForm.html_body}
                  onChange={e => setTemplateForm({...templateForm, html_body: e.target.value})}
                  placeholder="<html><body>...</body></html>"
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              <div className="grid gap-2">
                <Label>Text-Inhalt (optional)</Label>
                <Textarea
                  value={templateForm.plain_body}
                  onChange={e => setTemplateForm({...templateForm, plain_body: e.target.value})}
                  placeholder="Fallback für E-Mail-Clients ohne HTML"
                  rows={5}
                />
              </div>

              <div className="grid gap-2">
                <Label>Verfügbare Variablen (JSON)</Label>
                <Input
                  value={templateForm.available_variables}
                  onChange={e => setTemplateForm({...templateForm, available_variables: e.target.value})}
                  placeholder='["recipient_name", "amount", "due_date"]'
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveTemplate}>
                {editingTemplate ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
